import { Suspense, memo } from 'react';
import { Switch, useLocation } from 'react-router-dom';

import PropTypes from 'prop-types';

import ConfirmContactInfo from '../components/Auth/ConfirmContactInfoContainer';
import LoginContainer from '../components/Auth/LoginContainer';
import SetPasswordContainer from '../components/Auth/SetPasswordContainer';
import SignupContainer from '../components/Auth/SignupContainer';
import VerifyContainer from '../components/Auth/VerifyContainer';
import BuildingTableContainer from '../components/Building/BuildingTableContainer';
import EEHistoryPlaybackContainer from '../components/EagleEye/EEHistoryPlaybackContainer';
import MonthlyBillContainer from '../components/MonthlyBill/MonthlyBillContainer';
import PMCListContainer from '../components/PMC/PMCListContainer';

import DeliveryTabsComponent from '../components/Delivery/DeliveryTabsComponent';
import { CircularLoaderComponent } from '../components/LoadingSpinner/LoadingSpinnerComponent';
import SupportPageComponent from '../components/Page/SupportPageComponent';
import Component404 from '../components/Page/_404Component';
import VisitorRouterComponent from '../components/Visitor/VisitorRouterComponent';

import { lazyImport } from '../components/Main/utils';
// import Exports from '../components/Exports';
import { getHomeRoute } from './utils';

import AccessCard from '../components/AccessCard';
import AmenityRouter from '../components/Amenity/AmenityRouter';
import Logout from '../components/Auth/Logout';
import CommunityRouter from '../components/Community/CommunityRouter';
import DocumentLibrary from '../components/DocumentLibrary';
import { EventsRouter } from '../components/Events';
import IPManagementRouter from '../components/IPManagement/IPManagementRouter';
import ErrorBoundary from '../components/Main/ErrorBoundary';
import MessagesContainer from '../components/Messages';
import NPSContainer from '../components/NotificationPreferencesSettings';
import PrivacyPolicy from '../components/PrivacyPolicy';
import RemoteDoormanRouter from '../components/RemoteDoorman/RemoteDoormanRouter';
import { ModuleSkeleton } from '../components/Shared/skeletons';
import TermsAndConditions from '../components/TermsAndConditions';
import AdminRoute from './AdminRoute';
import CarsonActivityRoutes from './CarsonActivityRoutes';
import PersonsRoutes from './PersonsRoutes';
import PrivateRoute from './PrivateRoute';
import RedirectRoutes from './RedirectRoutes';
import Route from './SentryRoute';

const PMCRoutes = lazyImport(() => import('./PMCRoutes'), 'PMCRoutes');
const BuildingRoutes = lazyImport(
  () => import('./BuildingRoutes'),
  'BuildingRoutes',
);
const ServiceRequestRouter = lazyImport(
  () => import('../components/ServiceRequest/ServiceRequestRouter'),
  'ServiceRequestRouter',
);
const ExternalAccountsSettingsRoute = lazyImport(
  () => import('./ExternalAccountsSettingsRoute'),
  'ExternalAccountsSettingsRoute',
);

const moduleSkeletonPathnames = ['/pmc', '/building', '/service-requests'];

const Routes = ({ user }) => {
  const { pathname } = useLocation();

  const isModuleSkeleton = !!moduleSkeletonPathnames.find((path) =>
    pathname.includes(path),
  );

  return (
    <section className="content-area">
      <Suspense
        fallback={
          isModuleSkeleton ? <ModuleSkeleton /> : <CircularLoaderComponent />
        }
      >
        <ErrorBoundary>
          <Switch>
            <Route exact path="/" render={() => getHomeRoute(user)} />
            <Route path="/login" component={LoginContainer} />
            <Route path="/invite" component={SetPasswordContainer} />
            <Route path="/password-reset" component={SetPasswordContainer} />
            <Route path="/contacts/confirm" component={ConfirmContactInfo} />
            <Route path="/support" component={SupportPageComponent} />
            <Route path="/signup" component={SignupContainer} />
            <Route path="/verify" component={VerifyContainer} />
            <Route
              path="/terms-and-conditions"
              component={TermsAndConditions}
            />
            <Route path="/privacy-policy" component={PrivacyPolicy} />
            <AdminRoute
              path="/external-accounts-settings"
              component={ExternalAccountsSettingsRoute}
            />
            <AdminRoute
              path="/remote-doorman"
              component={RemoteDoormanRouter}
            />
            <PrivateRoute
              path="/ip-management"
              component={IPManagementRouter}
            />
            <PrivateRoute
              path="/service-requests"
              component={ServiceRequestRouter}
            />
            <PrivateRoute path="/visitors" component={VisitorRouterComponent} />
            <PrivateRoute path="/access-cards" component={AccessCard} />
            <PrivateRoute
              path="/deliveries"
              component={DeliveryTabsComponent}
            />
            <PrivateRoute
              path="/notification-preferences"
              component={NPSContainer}
            />

            <PrivateRoute path="/pmcs" component={PMCListContainer} exact />
            <PrivateRoute path="/pmcs" component={RedirectRoutes} />
            <PrivateRoute path="/pmc" component={PMCRoutes} />
            <PrivateRoute
              path="/buildings"
              component={BuildingTableContainer}
              exact
            />
            <PrivateRoute path="/buildings" component={RedirectRoutes} />
            <PrivateRoute path="/building" component={BuildingRoutes} />

            <PrivateRoute
              path="/global-person-search"
              component={PersonsRoutes}
            />
            <PrivateRoute path="/messages" component={MessagesContainer} />
            <PrivateRoute
              path="/document-library"
              component={DocumentLibrary}
              isRoot
            />
            <PrivateRoute
              path="/carson-activity"
              component={CarsonActivityRoutes}
            />
            <PrivateRoute
              path="/camera-history"
              component={EEHistoryPlaybackContainer}
            />
            {
              // <PrivateRoute path="/exports" component={Exports} />
            }
            <PrivateRoute
              path="/billing-report"
              component={MonthlyBillContainer}
            />
            <PrivateRoute path="/amenity" component={AmenityRouter} />
            <PrivateRoute path="/community" component={CommunityRouter} />
            <PrivateRoute path="/logout" component={Logout} />
            <PrivateRoute path="/events" component={EventsRouter} />
            <PrivateRoute path="*" component={Component404} />
          </Switch>
        </ErrorBoundary>
      </Suspense>
    </section>
  );
};

Routes.propTypes = {
  user: PropTypes.instanceOf(Object),
};

export default memo(Routes);
